import { FC, useEffect, useMemo, useState } from 'react';
import { Checkbox, Modal } from '@maxtropy/components';
import { Col, Row, Space } from 'antd';
import { DataProperty } from '@/api/reportStrategy';
import {
  apiV2AreaDeviceDataPropertyListPost,
  apiV2AreaDeviceDataPropertyRelationListPost,
  apiV2AreaDeviceDataPropertyRelationSavePost,
} from '@maxtropy/device-customer-apis-v2';

export interface CheckDataPropertyProps {
  areaId: number;
  deviceId?: number;
  open: boolean;
  onClose: () => void;
}

const CheckDataProperty: FC<CheckDataPropertyProps> = props => {
  const { deviceId, open, onClose, areaId } = props;
  const [allDataProperties, setAllDataProperties] = useState<DataProperty[]>([]);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [validCount, setValidCount] = useState<number>();

  const plainOptions = useMemo(() => {
    return (allDataProperties ?? []).map(i => ({
      label: i.name,
      value: i.id,
    }));
  }, [allDataProperties]);

  // 全选半选状态
  useEffect(() => {
    if (checkedIds.length === 0) {
      setIndeterminate(false);
      setCheckAll(false);
      return;
    }
    if (checkedIds.length) {
      setIndeterminate(checkedIds.length < plainOptions.length);
      setCheckAll(checkedIds.length === plainOptions.length);
    }
  }, [checkedIds, plainOptions.length]);

  // 获取所有数据点和已配置的数据点
  useEffect(() => {
    if (!deviceId || !open) return;

    Promise.all([
      apiV2AreaDeviceDataPropertyListPost({ deviceId }),
      apiV2AreaDeviceDataPropertyRelationListPost({ areaId, deviceId }),
    ]).then(([allDataRes, relationRes]) => {
      // 所有数据点（扁平化处理）
      const allList = allDataRes.list ?? [];
      const flatDataProperties: DataProperty[] = allList
        .flatMap(item =>
          (item.dataPropertySimpleList ?? []).map(prop => ({
            id: prop.id,
            name: prop.name,
          }))
        )
        .filter((prop, index, self) => index === self.findIndex(p => p.id === prop.id)); // 去重

      setAllDataProperties(flatDataProperties);

      // 已配置的数据属性 ID 集合（用于默认勾选）
      const relationList = relationRes.list ?? [];
      const configuredIds = relationList.map(item => item.dataPropertyId).filter(id => id !== undefined);

      // 计算有效的已配置数据点个数（存在于所有数据点中的）
      const validCount = configuredIds.filter(id => flatDataProperties.some(prop => prop.id === id)).length;

      setCheckedIds(configuredIds);
      setValidCount(validCount);
    });
  }, [deviceId, open]);

  const onLocalChange = (checkedValue: number[]) => {
    setCheckedIds(checkedValue);
  };

  const onCheckAllChange = () => {
    const allIds = allDataProperties.map(i => i.id!).filter(id => id !== undefined);
    const isAllChecked = checkedIds.length === allIds.length && allIds.length > 0;
    setCheckedIds(isAllChecked ? [] : allIds);
  };

  const handleOk = () => {
    const dataPropertyIds = checkedIds.filter(id => id !== undefined);
    apiV2AreaDeviceDataPropertyRelationSavePost({
      areaId,
      deviceId,
      dataPropertyIds,
    }).then(() => {
      onClose();
    });
  };

  return (
    <Modal
      title="监测数据点"
      contentClassName="modal-form-content"
      destroyOnClose
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText="保存"
      size="big"
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {(plainOptions ?? []).length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll}>
              全选
            </Checkbox>
            <span style={{ marginLeft: 16 }}>
              已选择 <span style={{ color: '#E64242', fontWeight: 500 }}>{validCount}</span> 项
            </span>
          </div>
        )}
        <Checkbox.Group style={{ width: '100%' }} value={checkedIds} onChange={onLocalChange}>
          <Row className="data-property-row" style={{ width: '100%' }} wrap>
            {plainOptions.map(i => (
              <Col key={i.value} span={6}>
                <Checkbox value={i.value}>{i.label}</Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Space>
    </Modal>
  );
};

export default CheckDataProperty;
